import React, { useMemo, useState } from 'react';
import { Button, Card, Input, message, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationDTO } from '@/dto/Application';
import { Status } from './Status';
import { FormattedDate } from './Date';
import Link from 'next/link';
import useSWR from 'swr';
import { useAuthRequest } from '@/hooks/use-request';
import { ContainerOutlined, CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { ApplicationType } from '@common/seller-application/enum/applicationType.enum';
import { ColumnProps, TablePaginationConfig } from 'antd/lib/table';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import {useAuth} from "@/hooks/use-auth";
import {Role} from "@common/seller-application/enum/role.enum";

export interface AdminListProps {
  dataStore?: ApplicationDTO[];
}

type FilterState = Record<string, React.Key[] | null>;
type SortState = { field?: string; order?: 'ascend' | 'descend' };

function getRoutePrefix(record: ApplicationDTO): string {
  if (record.applicationType === ApplicationType.NORMAL) {
    return 'admin';
  } else if (record.applicationType === ApplicationType.EXPRESS) {
    return 'express-admin';
  } else if (record.applicationType === ApplicationType.POS) {
    return 'pos-admin';
  }
  return 'admin';
}

function includesIgnoreCase(value: unknown, search: string): boolean {
  if (value == null) return false;
  return String(value).toLowerCase().includes(search.toLowerCase());
}

function statusI18nKey(status: string): string {
  return `application.status.${String(status).toLowerCase().replace(/_/g, '-')}`;
}

export const AdminList: React.FunctionComponent<AdminListProps> = ({ dataStore }: AdminListProps) => {
  const { t } = useTranslation();
  const { fetcher } = useAuthRequest();
  const { checkUserHasGroup, user } = useAuth();
  const isSales = checkUserHasGroup(Role.SALES);
  const userKey = user?.attributes?.sub || user?.username;
  const { data, mutate } = useSWR<ApplicationDTO[]>(userKey ? ['/application', userKey] : null, fetcher, {
    initialData: dataStore,
  });
  const { data: referralCode } = useSWR<string>(isSales && userKey ? ['/user/referral', userKey] : null, fetcher);

  const buildReferralUrl = (code: string): string => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/auth?pageState=signUp&referralCode=${encodeURIComponent(code)}`;
  };

  const [filters, setFilters] = useState<FilterState>({});
  const [sortedInfo, setSortedInfo] = useState<SortState>({});

  const searchLabel = t('common.search');
  const resetLabel = t('common.reset');

  const getColumnSearchProps = (dataIndex: keyof ApplicationDTO, placeholder: string): ColumnProps<ApplicationDTO> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button type='primary' onClick={() => confirm()} icon={<SearchOutlined />} size='small' style={{ width: 90, marginRight: 8 }}>
          {searchLabel}
        </Button>
        <Button
          onClick={() => {
            clearFilters?.();
            confirm();
          }}
          size='small'
          style={{ width: 90 }}>
          {resetLabel}
        </Button>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    filteredValue: filters[dataIndex] || null,
    onFilter: (value, record) => {
      const keyword = String(value ?? '');
      if (!keyword) return true;
      if (dataIndex === 'applicationNo') {
        const padded = String(record.applicationNo ?? '').padStart(8, '0');
        return includesIgnoreCase(padded, keyword) || includesIgnoreCase(record.applicationNo, keyword);
      }
      return includesIgnoreCase(record[dataIndex], keyword);
    },
  });

  const applicationTypeFilters = useMemo(() => {
    const types = Array.from(new Set((data || []).map(item => item.applicationType).filter(Boolean)));
    return types.map(type => ({ text: String(type), value: String(type) }));
  }, [data]);

  const statusFilters = useMemo(() => {
    const statuses = Array.from(new Set((data || []).map(item => item.status).filter(Boolean)));
    return statuses.map(status => ({
      text: t(statusI18nKey(String(status))),
      value: String(status),
    }));
  }, [data, t]);

  const columns: ColumnProps<ApplicationDTO>[] = [
    {
      dataIndex: 'applicationNo',
      key: 'applicationNo',
      title: t('application-list.application.no'),
      ...getColumnSearchProps('applicationNo', t('application-list.application.no')),
      render: (value: string, record: ApplicationDTO): JSX.Element => (
        <Link as={`/${getRoutePrefix(record)}/${record.id}`} href={`/${getRoutePrefix(record)}/[aid]`}>
          <a>{String(value).padStart(8, '0')}</a>
        </Link>
      ),
    },
    {
      dataIndex: 'mainlandCompanyName',
      key: 'mainlandCompanyName',
      title: t('application-list.application.mainlandcompanyname'),
      ellipsis: true,
      ...getColumnSearchProps('mainlandCompanyName', t('application-list.application.mainlandcompanyname')),
    },
    {
      dataIndex: 'hongkongCompanyName',
      key: 'hongkongCompanyName',
      title: t('application-list.application.hongkongcompanyname'),
      ellipsis: true,
      ...getColumnSearchProps('hongkongCompanyName', t('application-list.application.hongkongcompanyname')),
    },
    {
      dataIndex: 'applicationType',
      key: 'applicationType',
      title: t('application-list.application.applicationType'),
      filters: applicationTypeFilters,
      filteredValue: filters.applicationType || null,
      onFilter: (value, record) => String(record.applicationType) === String(value),
    },
    {
      dataIndex: 'createDate',
      key: 'createDate',
      title: t('application-list.application.create-date'),
      sorter: (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime(),
      sortOrder: sortedInfo.field === 'createDate' ? sortedInfo.order : null,
      render: (value): JSX.Element => <FormattedDate date={value} />,
    },
    {
      dataIndex: 'updateDate',
      key: 'updateDate',
      title: t('application-list.application.update-date'),
      sorter: (a, b) => new Date(a.updateDate).getTime() - new Date(b.updateDate).getTime(),
      sortOrder: sortedInfo.field === 'updateDate' ? sortedInfo.order : null,
      render: (value): JSX.Element => <FormattedDate date={value} />,
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: t('application-list.application.status'),
      filters: statusFilters,
      filteredValue: filters.status || null,
      onFilter: (value, record) => String(record.status) === String(value),
      render: (value): JSX.Element => <Status status={value} />,
    },
  ];

  return (
    <>
      <Card
        title={
          <>
            {t('application-list.title')} &nbsp;
            <small>{t('application-list.admin.mode')}</small>
            <div style={{ float: 'right' }}>
              <Tooltip placement='top' title={isSales && referralCode ? buildReferralUrl(referralCode) : undefined}>
                <Button
                  onClick={() => {
                    if (!isSales || !referralCode || typeof window === 'undefined') {
                      return;
                    }
                    navigator.clipboard.writeText(buildReferralUrl(referralCode)).then(() => message.success(t('common.operation.copy')));
                  }}
                  icon={<CopyOutlined />}
                  disabled={!isSales}
                  style={{ marginRight: '8px' }}
                  type='primary'>
                  {t('application-list.admin.sales.referral')}
                </Button>
              </Tooltip>
              {/* Partner link generation is no longer used
              <Link href='/admin/partner/referral'>
                <Button icon={<ContainerOutlined />} type='primary'>
                  {t('application-list.admin.partner.referral')}
                </Button>
              </Link>
              */}
            </div>
          </>
        }>
        <div style={{ margin: '20px 40px' }}>
          <Table<ApplicationDTO>
            loading={!data}
            dataSource={data}
            rowKey='id'
            columns={columns}
            pagination={{ position: 'bottomRight' } as TablePaginationConfig}
            onChange={(_pagination, nextFilters, sorter) => {
              setFilters(nextFilters as FilterState);
              const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
              setSortedInfo({
                field: singleSorter?.field ? String(singleSorter.field) : undefined,
                order: singleSorter?.order || undefined,
              });
            }}
          />
        </div>
      </Card>
    </>
  );
};
